(() => {
  'use strict';

  if (window.__pegasiSmoothScroll) {
    window.__pegasiSmoothScroll.refresh();
    return;
  }

  const gsap = window.gsap;
  if (!gsap) return;

  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopQuery = window.matchMedia('(min-width: 761px) and (pointer: fine)');
  const state = {
    current: window.scrollY,
    enabled: false,
    lastRendered: window.scrollY,
    running: false,
    target: window.scrollY,
  };

  const maxScroll = () => Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  const clampTarget = (value) => gsap.utils.clamp(0, maxScroll(), value);

  const stop = () => {
    if (!state.running) return;
    state.running = false;
    gsap.ticker.remove(render);
  };

  const render = (_time, deltaMs) => {
    const deltaSeconds = Math.min(deltaMs / 1000, .064);
    const amount = 1 - Math.exp(-17 * deltaSeconds);
    state.current += (state.target - state.current) * amount;

    if (Math.abs(state.target - state.current) < .25) state.current = state.target;
    state.lastRendered = state.current;
    // Flag this write so the scroll event it triggers is recognised as our own
    // and not mistaken for a native scroll (which would abort the animation).
    state.selfScrolling = true;
    window.scrollTo(0, state.current);

    if (state.current === state.target) stop();
  };

  const start = () => {
    if (!state.enabled || state.running) return;
    state.running = true;
    gsap.ticker.add(render);
  };

  const syncToNative = () => {
    // Ignore the scroll event our own render() write just produced. The old 2px
    // heuristic aborted here because device-pixel quantisation on high-DPI
    // displays pushed our own writes past 2px during fast scrolls.
    if (state.running && state.selfScrolling) {
      state.selfScrolling = false;
      return;
    }
    stop();
    const nativeY = window.scrollY;
    state.current = nativeY;
    state.target = nativeY;
    state.lastRendered = nativeY;
  };

  // getComputedStyle forces a style flush; caching the (essentially static)
  // overflow result keeps it out of the per-wheel-event hot path. Trackpads
  // fire 100+ Hz, so this ran thousands of forced recalcs per second before.
  const overflowScrollableCache = new WeakMap();
  const isOverflowScrollable = (node) => {
    let cached = overflowScrollableCache.get(node);
    if (cached === undefined) {
      const overflowY = getComputedStyle(node).overflowY;
      cached = overflowY === 'auto' || overflowY === 'scroll';
      overflowScrollableCache.set(node, cached);
    }
    return cached;
  };

  const hasScrollableAncestor = (origin, deltaY) => {
    let node = origin instanceof Element ? origin : null;
    while (node && node !== document.body && node !== document.documentElement) {
      if (isOverflowScrollable(node) && node.scrollHeight > node.clientHeight + 1) {
        const canMoveDown = deltaY > 0 && node.scrollTop < node.scrollHeight - node.clientHeight - 1;
        const canMoveUp = deltaY < 0 && node.scrollTop > 1;
        if (canMoveDown || canMoveUp) return true;
      }
      node = node.parentElement;
    }
    return false;
  };

  const normalizeWheel = (event) => {
    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaY * 18;
    if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaY * window.innerHeight;
    return event.deltaY;
  };

  const onWheel = (event) => {
    if (!state.enabled || event.defaultPrevented || event.ctrlKey) return;
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
    if (hasScrollableAncestor(event.target, event.deltaY)) return;

    const delta = gsap.utils.clamp(-180, 180, normalizeWheel(event));
    if (!delta) return;
    event.preventDefault();

    if (!state.running) {
      state.current = window.scrollY;
      state.target = window.scrollY;
    }
    state.target = clampTarget(state.target + delta);
    start();
  };

  const scrollToTarget = (value) => {
    if (!state.enabled) {
      window.scrollTo({ top: value, behavior: reduceMotionQuery.matches ? 'auto' : 'smooth' });
      return;
    }
    state.current = window.scrollY;
    state.target = clampTarget(value);
    start();
  };

  const onAnchorClick = (event) => {
    const link = event.target.closest?.('a[href^="#"]');
    if (!link || link.getAttribute('href') === '#') return;
    if (event.detail === 0) return;
    const target = document.querySelector(link.getAttribute('href'));
    if (!target) return;
    event.preventDefault();
    const top = target.getBoundingClientRect().top + window.scrollY;
    scrollToTarget(top);
    history.replaceState(null, '', link.hash);
  };

  const refresh = () => {
    const needsNativeScroll = Boolean(document.querySelector('[data-pgc-root], .pg-pdp'));
    state.enabled = !needsNativeScroll && desktopQuery.matches && !reduceMotionQuery.matches;
    document.documentElement.classList.toggle('pg-scroll-smooth', state.enabled);
    state.current = window.scrollY;
    state.target = clampTarget(window.scrollY);
    state.lastRendered = window.scrollY;
    if (!state.enabled) stop();
  };

  const onVisibilityChange = () => {
    if (document.hidden) {
      stop();
      return;
    }
    syncToNative();
  };

  window.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('scroll', syncToNative, { passive: true });
  window.addEventListener('resize', refresh, { passive: true });
  window.addEventListener('pagehide', stop, { passive: true });
  document.addEventListener('click', onAnchorClick);
  document.addEventListener('visibilitychange', onVisibilityChange);
  reduceMotionQuery.addEventListener?.('change', refresh);
  desktopQuery.addEventListener?.('change', refresh);

  window.__pegasiSmoothScroll = { refresh, scrollTo: scrollToTarget };
  refresh();
})();
